
import axios from 'axios';

import _keyBy from 'lodash/keyBy';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  onMounted,
} from 'vue';

import { useToast } from 'primevue/usetoast';

import type {
  Endpoint,
  FeedCreative,
} from '@/types';

import { REGIONS_MAP } from '@/libs/consts';

export default defineComponent({
  components: {
    FeedCreativeCard: defineAsyncComponent(() => import('@/components/FeedCreativeCard.vue')),
  },
  setup() {
    const toast = useToast();

    const clientContext = ref(null);

    const endpointId = ref<number | null>(null);

    const subid = ref<string>('default');

    const feedsIds = ref<number[]>([]);

    const feedsCreatives = ref<FeedCreative[]>([]);

    const feedsCreativesLoading = ref(false);

    const feedOptionsMap = ref({});

    const fetchCreatives = async (): Promise<void> => {
      feedsCreativesLoading.value = true;

      feedsCreatives.value = [];

      feedOptionsMap.value = {};

      try {
        const endpointResponse = await axios.post('/api/endpoint/getEndpointById', { id: endpointId.value });

        const endpoint = endpointResponse.data as Endpoint;

        const params = {
          endpoint_uuid: endpoint.uuid,
          subid: subid.value,
          referrer: `https://${endpoint.project.host}`,
          timeout: 1000,
          trust_headers: true,
          feed_response_preview: true,
          feed_id: feedsIds.value.length ? feedsIds.value.join(',') : undefined,
          show_traffic_type: true,
        };

        // Для запроса нужен домен который работает по https, в push_host как правило такой домен, в ssp_host нет
        const host = `${REGIONS_MAP[endpoint.region].subdomain}${endpoint.project.push_host}`;

        const response = await axios.get(`https://${host}/endpoint`, { params });

        if (response.status === 200) {
          feedsCreatives.value = response.data;

          const uniqueFeedsIds = Array.from(new Set(feedsCreatives.value.map((creative) => creative.feed_id)));

          const feedsOptionsResponse = await axios.get('/api/v3/feeds/options', {
            params: {
              'filter[id]': uniqueFeedsIds.join(','),
              include: 'project',
            },
          });

          feedOptionsMap.value = _keyBy(feedsOptionsResponse.data.data, 'id');
        }

        if (response.status === 204) {
          toast.add({
            severity: 'info',
            summary: 'Info',
            detail: 'No ads',
            life: 2000,
          });
        }
      } catch (error) {
        console.error(error);

        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch creatives',
          life: 2000,
        });
      }

      feedsCreativesLoading.value = false;
    };

    const fetchClientContext = async (): Promise<void> => {
      try {
        const response = await axios.get('/api/v3/help/client-context');

        clientContext.value = response.data;
      } catch (error) {
        console.error(error);

        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch client context',
          life: 2000,
        });
      }
    };

    onMounted(() => fetchClientContext());

    return {
      clientContext,
      endpointId,
      subid,
      feedsIds,
      feedsCreatives,
      feedsCreativesLoading,
      feedOptionsMap,
      fetchCreatives,
    };
  },
});
